import { DatePipe } from "@angular/common";
import { Injectable } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { TenantUser } from "../view-model/auth.view-model";
import { QueryCategory, QueryConstants } from "../modules/query-apbuilder/query.constants";
import { IChildNotification, ITenantInfoDTO } from "../view-model/query-builder.view-model";
import { DataQueryBuilderService } from "../modules/query-apbuilder/services/data-query-builder.service";
import { AppConstants } from "../app.constants";

@Injectable()
export class DataService {
  screenLoaderDataSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  screenLoaderDataSubjectCast = this.screenLoaderDataSubject.asObservable();
  private formSavedData = new Subject<any>();
  childPopupWindow!: Window | null;
  isChildPopupWindowOpened = false;
  constructor(private datePipe: DatePipe, private dataQueryBuilderService:DataQueryBuilderService) {}

  public getFormData(): Observable<any> {
    return this.formSavedData.asObservable();
  }

  public setFormData(formData: any) {
    return this.formSavedData.next(formData);
  }

  public getTableDateFormat(date: any, format: string) {
    return this.datePipe.transform(date, format);
  }

  public isNumber(num: any): boolean {
    return !isNaN(parseFloat(num)) && isFinite(num);
  }
  
  getValidators(validators: any) {
    const validatorsToAdd: ValidatorFn | ValidatorFn[] | null | undefined = [];
    if (validators != null) {
      for (const [key, value] of Object.entries(validators)) {
        switch (key) {
          case "required":
            if (value as boolean) {
              validatorsToAdd.push(Validators.required);
            }
            break;
          case "email":
            if (value as boolean) {
              validatorsToAdd.push(Validators.email);
            }
            break;
          case "minlength":
            validatorsToAdd.push(Validators.minLength(value as number));
            break;
          case "maxlength":
            validatorsToAdd.push(Validators.maxLength(value as number));
            break;
          case "patternValidation":
            validatorsToAdd.push(Validators.pattern(value as string));
            break;
          case "whiteSpaceValidation":
            if (value as boolean) {
              validatorsToAdd.push(Validators.pattern(QueryConstants.whiteSpaceRegex));
            }
            break;
          case "bitDataTypeValidation":
            if (value as boolean) {
              validatorsToAdd.push(this.bitDataTypeValidation());
            }
            break;
          case "customValidators":
            let customValidatorFunctions = value as ValidatorFn[] ?? [];
            if (customValidatorFunctions.length > 0) customValidatorFunctions.forEach((validator: ValidatorFn) => validatorsToAdd.push(validator));
            break;
        }
      }
    }
    return validatorsToAdd;
  }
  
  /**
  * Custom validator to set to a control to validate tableName alias
  * Input: optional tableID ,
  * validates table name and returns error message if its invalid
  * syntax to add this validator to a conrol:
  *  validators: { customValidators : [this.dataService.customTableAliasValidator(0)] },
  */
  customTableAliasValidator(queryTableId: number = 0): ValidatorFn {
    return (control: AbstractControl) => {
      return this.dataQueryBuilderService.tableAliasExistsInQuery(control.value?.toString(), queryTableId) ? { customValidator: { message: "The alias you entered is already being used by another table." } } : null;
    };
  }

  bitDataTypeValidation(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      
      if(value.toString().length > 1){
        return { bitDataTypeValidation: true };
      }
      else if (value !== null && value !== undefined && Number(value) !== 1 && Number(value) !== 0) {
        return { bitDataTypeValidation: true };
      }
      return null;
    };
  }

  validateAllFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFields(control);
      }
    });
  }

  get currentUser(): TenantUser | null {
    // return this.helper.decodeToken(this._token);
    // return this._currentUser;
    const user =
      sessionStorage.getItem("user") != null ||
      sessionStorage.getItem("user") != ""
        ? sessionStorage.getItem("user")
        : "";
    if (user != null) return JSON.parse(user);
    return null;
  }

  set currentUser(user: TenantUser | null) {
    sessionStorage.setItem("user", JSON.stringify(user));
  }

  set queryId(queryId : number){
    if (queryId > 0) {
      sessionStorage.setItem("queryId", JSON.stringify(queryId));
    }
  }
  get queryId(): number{
    const id = sessionStorage.getItem("queryId") ?? "0";
    return JSON.parse(id);
  }
  removeQueryID(){
    sessionStorage.removeItem("queryId");
  }

  fetchDifferenceInDays(futureDate: any, currentDate: any) {
    const date1: any = new Date(currentDate); // yyyy-MM-dd
    const date2: any = new Date(futureDate); // yyyy-MM-dd
    const difference: any = Math.abs(date2 - date1);
    const days: any = difference / (1000 * 3600 * 24);
    return days;
  }

  /* get & set tenant info  */
  set tenantInfo(tenantInfoDTO : ITenantInfoDTO | null){
      sessionStorage.setItem("tenantInfo", JSON.stringify(tenantInfoDTO));
  }

  get tenantInfo(): ITenantInfoDTO | null {
    const tenantInfo =
      sessionStorage.getItem("tenantInfo") != null ||
      sessionStorage.getItem("tenantInfo") != ""
        ? sessionStorage.getItem("tenantInfo")
        : "";
    if (tenantInfo != null) return JSON.parse(tenantInfo);
    return null;
  }

  get parentQueryId(): number {
    return Number(sessionStorage.getItem("parentQueryId"));
  }

  get childWindowQueryCategory(): QueryCategory | null {
    return Number(sessionStorage.getItem("childWindowQueryCategory")) !== 0 ? Number(sessionStorage.getItem("childWindowQueryCategory")) : null;
  }
  set childWindowQueryCategory(queryCategory : number | null) {
    sessionStorage.setItem('childWindowQueryCategory',JSON.stringify(queryCategory));
  }
  
  get isChildPopupWindow(): boolean {
    return sessionStorage.getItem("parentQueryId") != null;
  }

  set parentQueryId(queryId: number) {
    sessionStorage.setItem("parentQueryId", JSON.stringify(queryId));
  }

  get isChildPopupWindowOpen(): boolean {
    return this.isChildPopupWindowOpened;
  }

  set IsChildWindowOpen(value: boolean) {
    this.isChildPopupWindowOpened = value;
  }

  get isLocalPopupOpen(): boolean{
    const val = sessionStorage.getItem("isLocalPopup");
    if(val == null) return false;
    return val.toLowerCase() === 'true';
  }
  set isLocalPopupOpen(isOpen: boolean) {
    sessionStorage.setItem("isLocalPopup", JSON.stringify(isOpen));
  }
  
  setLocalPopupState({ isOpen, window }: { isOpen: boolean; window: Window }) {
    window.sessionStorage.setItem("isLocalPopup", JSON.stringify(isOpen));
  }

  /**
   * 
   * @returns child window instance as is 
   */
  getChildPopupWindow() {
    return this.childPopupWindow;
  }

  /**
  * Opens new child window pointing to input URL
  * input : URL of child window.
  * @returns child window instance,
  */
  openChildPopupWindow(url: string) {
    if (this.isChildPopupWindowOpened === true && this.childPopupWindow) {
      // dont open 2nd child window.
      return this.childPopupWindow;
    }
    else {
      const width = window.screen.width;
      const height = window.screen.height;
      this.childPopupWindow = window.open(url, "_blank", `width=${width},height=${height}`);
      if (this.childPopupWindow) {
        this.isChildPopupWindowOpened = true;
        // this will be listening mmessages from child window.  
        // child has tpo post message  window.postMessage('Hello from the new window!','*');
        this.childPopupWindow.addEventListener("message", (event) => {
          if (event.data) {
            this.dataQueryBuilderService.childNotificationData.next(event.data);
          }
          // Handle the received message from the new window
        });
      }
      return this.childPopupWindow;
    }
  }

  closeChildPopupWindow() {
    if (this.childPopupWindow) {
      this.childPopupWindow.close();
      this.isChildPopupWindowOpened = false;
    }
  }

  postMessageChildWindow(childNotification: IChildNotification) {
    window.postMessage(childNotification, AppConstants.server_Url);
  }

  windowClose() {
    window.close();
  }
}
